import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  mdiAccountOutline,
  mdiCameraPlus,
  mdiEmailOutline,
  mdiInformationOutline,
  mdiPhoneOutline
} from "@mdi/js";
import Icon from "@mdi/react";
import * as Yup from "yup";

import Button from "components/Button";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Header from "components/Header";
import Input from "components/Input";
import PhoneNumber from "components/PhoneNumber";
import Radio from "components/Radio";
import { routes } from "constants/routes";
import { formatPhoneNumber } from "helpers/formatPhoneNumber";
import { faxRegex, phoneRegex } from "helpers/regex";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import MainLayout from "layouts/Main";
import { AddressInfo, IUserProfileForm, UserAccount, UserProfileFormData } from "models/User";
import { AppDispatch, RootState } from "store";
import { updateUserProfile } from "store/user/actions";
import styles from "./style.module.scss";

const userProfileFormSchema = Yup.object<Partial<IUserProfileForm>>()
  .shape({
    mc: Yup.string().nullable(),
    dot: Yup.string().nullable(),
    contact_phone: Yup.string()
      .nullable()
      .max(19, "Phone number limit exceeded")
      .matches(phoneRegex, { message: "Phone number is not valid", excludeEmptyString: true }),
    fax_number: Yup.string()
      .nullable()
      .max(19, "Phone number limit exceeded")
      .matches(faxRegex, { message: "Fax number is not valid", excludeEmptyString: true }),
    url: Yup.string().url("Url is not valid").nullable(),
    cargo_insurance_value: Yup.string().required("This field is required")
  })
  .test("at-least-one", "Please enter an MC or DOT", function (value) {
    const { mc, dot } = value;
    const isValid = !!mc || !!dot;
    if (!isValid) {
      return this.createError({ path: "mc", message: "Please enter an MC # or DOT #" });
    }
    return true;
  });

export default function UserSettingProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [avatar, setAvatar] = useState<File>();
  const { account } = useSelector((state: RootState) => state.authState);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setAvatar(e.target.files[0]);
    }
  };

  const { userDetails } = useSelector((state: RootState) => state.userState);

  const {
    email,
    contact_name,
    contact_phone,
    fax_number,
    url,
    dba,
    address_object,
    mc,
    dot,
    cargo_insurance_provider,
    cargo_insurance_value,
    language: defaultLanguage,
    about
  } = useMemo<Partial<UserAccount>>(() => {
    if (userDetails) return userDetails;
    else {
      return {};
    }
  }, [userDetails]);

  const {
    control,
    register,
    setValue,
    formState: { errors, isSubmitting },
    reset,
    watch,
    handleSubmit
  } = useForm<Partial<IUserProfileForm>>({
    resolver: yupResolver(userProfileFormSchema) as any
  });

  const selectedLang = watch("language");

  const goProfile = () => {
    navigate(`${routes.USER}/${userDetails.id}`);
  };

  const onSubmit = async (formData: IUserProfileForm) => {
    const {
      owners_first_name,
      owners_last_name,
      contact_phone,
      fax_number,
      url,
      dba,
      addressData,
      mc,
      dot,
      cargo_insurance_provider,
      cargo_insurance_value,
      about,
      language
    } = formData;

    const userProfileData: { type: string; userId: string } & UserProfileFormData = {
      type: account.type,
      userId: account[account.type as keyof typeof account],
      ...(avatar !== undefined && { image: avatar }),
      contact_name: owners_first_name + " " + owners_last_name,
      contact_phone,
      fax_number,
      url,
      dba,
      address: addressData.address ?? "",
      city: addressData.city ?? "",
      state: addressData.state ?? "",
      country: addressData.country ?? "",
      zip: addressData.zip ?? "",
      mc,
      dot,
      cargo_insurance_provider: cargo_insurance_provider ?? "",
      cargo_insurance_value,
      about,
      language
    };

    await dispatch(updateUserProfile(userProfileData))
      .then(() => {
        showSuccessMessage("Successfully updated profile.");
      })
      .catch(() => {
        showErrorMessage("Error updating profile.");
      });
  };

  useEffect(() => {
    reset({
      user_type: account.type,
      email: email,
      owners_first_name: contact_name?.split(" ")[0],
      owners_last_name: contact_name?.split(" ")[1],
      contact_phone: contact_phone,
      fax_number: fax_number,
      url: url,
      dba: dba,
      addressData: address_object,
      mc: mc,
      dot: dot,
      cargo_insurance_provider: cargo_insurance_provider,
      cargo_insurance_value: cargo_insurance_value,
      about: about,
      language: defaultLanguage
    });
  }, []);

  return (
    <MainLayout
      desktopTitle="Profile"
      customHeader={
        <Header
          label="Profile"
          headerIcon={<Icon path={mdiInformationOutline} />}
          goBackPath={routes.SETTINGS}
          isHideDesktop
        />
      }
      hideNavigation
      className="px-5 pb-6">
      <form className={styles.userProfileContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.profileCard}>
          <div className={styles.avatarWrapper}>
            {avatar ? (
              <img src={URL.createObjectURL(avatar)} alt="avatar" className="m-auto rounded-full" />
            ) : (
              <Icon path={mdiAccountOutline} className="m-auto size-10" />
            )}
          </div>
          <div className={styles.cardBody}>
            <h3 className={styles.contactName}>{contact_name}</h3>
            <div className={styles.detail}>
              <Icon path={mdiPhoneOutline} className="size-3 mr-1.5" />
              <span>
                {formatPhoneNumber(typeof contact_phone === "string" ? contact_phone : "")}
              </span>
            </div>
            <div className={styles.detail}>
              <Icon path={mdiEmailOutline} className="size-3 mr-1.5 flex-none" />
              <span className="break-all">{email}</span>
            </div>
          </div>
          <label className={styles.addAvatar}>
            <input type="file" accept="image/*" onChange={handleFileChange} name="avatar" hidden />
            <Icon path={mdiCameraPlus} className="size-3.5" />
          </label>
        </div>

        <div className={styles.profileForm}>
          <Input
            label="&nbsp;&nbsp;Owner’s Type"
            name="user_type"
            register={register}
            inputClass="capitalize"
            disabled
          />
          <Input
            type="email"
            name="email"
            label="&nbsp;&nbsp;Owner’s Email (for Haul Pay notifications)"
            register={register}
            disabled
          />

          <div className={styles.ownerName}>
            <Input
              label="&nbsp;&nbsp;Owner’s First Name"
              name="owners_first_name"
              register={register}
              className="flex-1"
            />
            <Input
              label="&nbsp;&nbsp;Owner’s Last Name"
              name="owners_last_name"
              className="flex-1"
              register={register}
            />
          </div>

          <Controller
            name="contact_phone"
            control={control}
            render={({ field: { value = "", onChange } }) => (
              <PhoneNumber
                value={value}
                onChange={onChange}
                label="&nbsp;&nbsp;Owner’s Cell Phone"
                error={errors.contact_phone}
                wrapperClass="flex-1"
              />
            )}
          />
          <Input
            label="&nbsp;&nbsp;Fax"
            name="fax_number"
            register={register}
            error={errors.fax_number}
          />
          <Controller
            name="addressData"
            control={control}
            render={({ field: { value, onChange } }) => (
              <GoogleAutoComplete
                label="&nbsp;&nbsp;Business Address"
                value={value}
                onChange={(addressInfo: AddressInfo) => onChange(addressInfo)}
              />
            )}
          />
          <Input type="url" label="&nbsp;&nbsp;Website" name="url" register={register} />
          <Input label="&nbsp;&nbsp;Business Name" name="dba" register={register} />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;MC #"
            name="mc"
            register={register}
            error={errors.mc}
          />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;DOT #"
            name="dot"
            register={register}
            error={errors.dot}
          />
          <Input
            label="&nbsp;&nbsp;Cargo Insurance Provider"
            name="cargo_insurance_provider"
            register={register}
          />
          <Input
            type="number"
            inputMode="numeric"
            label="&nbsp;&nbsp;Cargo Insurance Value"
            name="cargo_insurance_value"
            register={register}
            error={errors.cargo_insurance_value}
          />
          <Input label="&nbsp;&nbsp;More Info" name="about" register={register} />

          <div className={styles.languagePreference}>
            <span className="text-sm text-text">Language preference?</span>

            <div className="flex gap-4">
              <Radio
                label="EN"
                name="language"
                value="en"
                onChange={(event) => setValue("language", event.target.value)}
                className="text-placeholder"
                checked={selectedLang === "en"}
              />
              <Radio
                label="ES"
                name="language"
                value="es"
                onChange={(event) => setValue("language", event.target.value)}
                className="text-placeholder"
                checked={selectedLang === "es"}
              />
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-none desktop:!self-end mobile:flex-1 desktop:!w-56 desktop:!h-12"
            onClick={goProfile}>
            View Profile
          </Button>
          <Button
            type="submit"
            size="lg"
            className="flex-none desktop:!self-end mobile:flex-1 desktop:!w-56 desktop:!h-12"
            loading={isSubmitting}>
            Save
          </Button>
        </div>
      </form>
    </MainLayout>
  );
}
